// aldoo-ra/Admin/Logs/schema.js
// Logs schema for easy migration and manipulation

export const LOG_LEVELS = {
  INFO: 'info',
  WARN: 'warn', 
  ERROR: 'error'
}

export const DEFAULT_CHANNELS = [
  { 
    id: 'database', 
    name: 'Database', 
    enabled: true, 
    color: '#FF6B6B', 
    messageCount: 0, 
    messagesPerMinute: 0, 
    enabledLevels: [LOG_LEVELS.INFO, LOG_LEVELS.WARN, LOG_LEVELS.ERROR] 
  },
  { 
    id: 'system', 
    name: 'System', 
    enabled: true, 
    color: '#4ECDC4', 
    messageCount: 0, 
    messagesPerMinute: 0, 
    enabledLevels: [LOG_LEVELS.INFO, LOG_LEVELS.WARN, LOG_LEVELS.ERROR] 
  },
  { 
    id: 'api', 
    name: 'API', 
    enabled: true, 
    color: '#4285F4', 
    messageCount: 0, 
    messagesPerMinute: 0, 
    enabledLevels: [LOG_LEVELS.INFO, LOG_LEVELS.WARN, LOG_LEVELS.ERROR] 
  },
  { 
    id: 'licensing', 
    name: 'Licensing', 
    enabled: true, 
    color: '#9C27B0', 
    messageCount: 0, 
    messagesPerMinute: 0, 
    enabledLevels: [LOG_LEVELS.INFO, LOG_LEVELS.WARN, LOG_LEVELS.ERROR] 
  },
  { 
    id: 'subscriptions', 
    name: 'Subscriptions', 
    enabled: true, 
    color: '#FF9800', 
    messageCount: 0, 
    messagesPerMinute: 0, 
    enabledLevels: [LOG_LEVELS.INFO, LOG_LEVELS.WARN, LOG_LEVELS.ERROR] 
  },
  { 
    id: 'google-play', 
    name: 'Google Play', 
    enabled: true, 
    color: '#4CAF50', 
    messageCount: 0, 
    messagesPerMinute: 0, 
    enabledLevels: [LOG_LEVELS.INFO, LOG_LEVELS.WARN, LOG_LEVELS.ERROR] 
  },
  { 
    id: 'apple-store', 
    name: 'Apple Store', 
    enabled: true, 
    color: '#007AFF', 
    messageCount: 0, 
    messagesPerMinute: 0, 
    enabledLevels: [LOG_LEVELS.INFO, LOG_LEVELS.WARN, LOG_LEVELS.ERROR] 
  }
]

export const LOG_ENTRY_SCHEMA = {
  id: 'string', // unique identifier
  timestamp: 'number', // unix timestamp
  data: 'string', // log message
  structuredData: 'object', // structured context data (optional)
  channels: 'array', // array of channel ids
  color: 'string', // display color (from channel)
  level: 'string', // LOG_LEVELS value 
  callstack: 'string', // optional callstack trace
  count: 'number', // for compacted logs (default 1)
  startTime: 'number', // for compacted logs
  endTime: 'number' // for compacted logs
}

export const REALTIME_CONFIG = {
  // Configuration for aldoo-ra/realtime integration
  namespace: 'logs', // Socket.io namespace for /rt/logs
  reconnectInterval: 5000,
  maxReconnectAttempts: 10,
  bufferSize: 200, // max logs to keep in memory
  compactionEnabled: true
}

// Removed TIME_RANGES and DEFAULT_TIME_RANGE - no longer filtering by date

export const UI_CONFIG = {
  searchPlaceholder: 'Search log messages...',
  filterPlaceholder: 'Filter out (comma-separated)...',
  maxDisplayLogs: 200,
  autoScrollThreshold: 10, // pixels from bottom to trigger auto-scroll
  compactionWindow: 100, // ms window for message compaction
  channelSearchPlaceholder: 'Search channels...'
}

// Test data patterns for development
export const TEST_PATTERNS = [
  { 
    message: 'Server startup complete', 
    channel: 'system', 
    repeat: 1, 
    level: LOG_LEVELS.INFO,
    callstack: 'at ServerBootstrap.start (server.js:45:12)\n    at Application.boot (app.js:23:8)\n    at main (index.js:15:5)'
  },
  { 
    message: 'Heartbeat check', 
    channel: 'system', 
    repeat: 5, 
    level: LOG_LEVELS.INFO,
    callstack: 'at HealthMonitor.check (monitor.js:78:15)\n    at setInterval (timer.js:12:3)'
  },
  { 
    message: 'Database connection established successfully with connection pool size of 10, timeout configured to 30 seconds, and retry mechanism enabled for handling temporary connection failures', 
    channel: 'database', 
    repeat: 1, 
    level: LOG_LEVELS.INFO,
    callstack: 'at DatabaseManager.connect (db.js:156:20)\n    at ConnectionPool.initialize (pool.js:89:14)\n    at DatabaseService.start (service.js:34:7)'
  },
  { 
    message: 'Cache hit', 
    channel: 'database', 
    repeat: 8, 
    level: LOG_LEVELS.INFO,
    callstack: 'at RedisCache.get (cache.js:45:8)\n    at DataService.fetch (data.js:23:12)'
  },
  { 
    message: 'Processing incoming webhook from payment provider Stripe with event type payment_intent.succeeded, amount $149.99, customer ID cus_ABC123, attempting to update order status and send confirmation email to customer', 
    channel: 'api', 
    repeat: 1, 
    level: LOG_LEVELS.INFO,
    callstack: 'at WebhookHandler.process (webhook.js:234:16)\n    at PaymentProcessor.handleStripe (payment.js:67:9)\n    at ExpressRouter.post (routes.js:45:13)',
    structuredData: {
      provider: 'stripe',
      eventType: 'payment_intent.succeeded',
      amount: 149.99,
      currency: 'USD',
      customerId: 'cus_ABC123',
      paymentIntent: 'pi_1234567890abcdef',
      metadata: {
        orderId: 'order_789',
        customerEmail: 'customer@example.com',
        productIds: ['prod_123', 'prod_456']
      }
    }
  },
  { 
    message: 'Queue size: 12', 
    channel: 'system', 
    repeat: 3, 
    level: LOG_LEVELS.WARN,
    callstack: 'at QueueMonitor.checkSize (queue.js:98:11)\n    at WorkerManager.monitor (worker.js:145:7)'
  },
  { 
    message: 'User login failed', 
    channel: 'api', 
    repeat: 1, 
    level: LOG_LEVELS.WARN,
    callstack: 'at AuthService.authenticate (auth.js:87:14)\n    at LoginController.handleLogin (login.js:56:9)\n    at ExpressRouter.post (routes.js:23:5)'
  },
  { 
    message: 'Memory usage: 67%', 
    channel: 'system', 
    repeat: 12, 
    level: LOG_LEVELS.WARN,
    callstack: 'at SystemMonitor.checkMemory (monitor.js:123:19)\n    at ResourceWatcher.poll (watcher.js:78:15)'
  },
  { 
    message: 'Critical error occurred during batch processing job for customer data synchronization: Unable to connect to external CRM system after 5 retry attempts, affecting 2,847 customer records scheduled for update', 
    channel: 'database', 
    repeat: 1, 
    level: LOG_LEVELS.ERROR,
    callstack: 'at CRMSyncService.syncCustomers (crm.js:456:23)\n    at BatchProcessor.processChunk (batch.js:234:12)\n    at DataSyncJob.execute (jobs.js:89:8)\n    at JobScheduler.runJob (scheduler.js:167:14)'
  },
  { 
    message: 'API rate limit exceeded', 
    channel: 'api', 
    repeat: 6, 
    level: LOG_LEVELS.WARN,
    callstack: 'at RateLimiter.checkLimit (limiter.js:67:10)\n    at ApiMiddleware.enforce (middleware.js:34:7)\n    at ExpressRouter.use (routes.js:12:3)'
  },
  { 
    message: 'Backup process started', 
    channel: 'system', 
    repeat: 1, 
    level: LOG_LEVELS.INFO,
    callstack: 'at BackupService.start (backup.js:78:13)\n    at CronJob.execute (cron.js:45:9)\n    at ScheduleManager.run (schedule.js:123:6)'
  },
  { 
    message: 'Processing transaction validation for payment method ending in 4532, amount $299.99, merchant verification passed, fraud detection score 0.23, proceeding with authorization request to payment gateway', 
    channel: 'api', 
    repeat: 2, 
    level: LOG_LEVELS.INFO,
    callstack: 'at PaymentValidator.validate (validator.js:145:18)\n    at TransactionProcessor.process (transaction.js:89:12)\n    at PaymentGateway.authorize (gateway.js:234:8)'
  },
  { 
    message: 'Database connection timeout', 
    channel: 'database', 
    repeat: 1, 
    level: LOG_LEVELS.ERROR,
    callstack: 'at Connection.timeout (connection.js:234:15)\n    at ConnectionPool.acquire (pool.js:123:9)\n    at DatabaseService.query (service.js:78:11)\n    at UserRepository.findById (repository.js:45:7)'
  },
  { 
    message: 'Invalid authentication token', 
    channel: 'api', 
    repeat: 1, 
    level: LOG_LEVELS.ERROR,
    callstack: 'at TokenValidator.verify (token.js:89:12)\n    at AuthMiddleware.authenticate (auth.js:56:8)\n    at ExpressRouter.use (routes.js:34:5)\n    at Application.handle (app.js:123:11)'
  },
  { 
    message: '📊 License synchronization completed', 
    channel: 'licensing', 
    repeat: 1, 
    level: LOG_LEVELS.INFO,
    callstack: 'at ProductLicensing.syncLicenseStatusWithSubscription (index.js:702:16)\n    at GooglePlayEventAdapter.syncLicenseStatus (event-adapter.js:34:12)\n    at GooglePlayEventAdapter.handleCancellation (event-adapter.js:427:8)',
    structuredData: {
      subscriptionId: '686943ab5ca6bb7f37ef1db0',
      subscriptionStatus: 'canceled',
      licenseStatus: 'canceled',
      syncedCount: 2,
      totalLicenses: 3,
      userId: '672261b62d9ed95689baff66',
      source: 'google_play_adapter'
    }
  },
  { 
    message: '🔄 Processing subscription cancellation', 
    channel: 'google-play', 
    repeat: 1, 
    level: LOG_LEVELS.INFO,
    callstack: 'at GooglePlayEventAdapter.handleCancellation (event-adapter.js:408:8)\n    at GooglePlayEventAdapter.processEvent (event-adapter.js:119:12)\n    at WebhookController.handleGooglePlay (webhook.js:45:9)',
    structuredData: {
      userId: '672261b62d9ed95689baff66',
      subscriptionId: '686943ab5ca6bb7f37ef1db0',
      status: 'canceled',
      accessNote: 'User maintains access until expiry per Google Play policy',
      source: 'google_play_adapter',
      platform: 'android',
      notificationType: 3,
      purchaseToken: 'unique_purchase_token_123'
    }
  }
]